import React from "react";
import {
  Activity,
  FileText,
  Upload,
  UserPlus,
  Folder,
  Link as LinkIcon,
  Download,
  Trash2,
  Eye,
  Shield,
  Calendar,
} from "lucide-react";
import { AuditLogEntry } from "../../../types/audit";
import { Card } from "../Card/Card";
import { toTitleCase } from "@secure-caseworker/utils";

export type ActivityLogItemVariant = "default" | "minimal" | "audit";

interface ActivityLogItemProps {
  entry: AuditLogEntry;
  variant?: ActivityLogItemVariant;
  showMetadata?: boolean;
  showChanges?: boolean;
  showIpAddress?: boolean;
}

const actionIcons: Record<string, any> = {
  create: FileText,
  upload: Upload,
  download: Download,
  delete: Trash2,
  share: LinkIcon,
  view: Eye,
  invite: UserPlus,
  "access-granted": Shield,
};

const actionColorClasses: Record<string, string> = {
  create:
    "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400",
  upload:
    "text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400",
  download:
    "text-indigo-600 bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800 dark:text-indigo-400",
  delete:
    "text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400",
  share:
    "text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400",
  view: "text-cyan-600 bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800 dark:text-cyan-400",
  invite:
    "text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-400",
  "access-granted":
    "text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400",
};

export const ActivityLogItem: React.FC<ActivityLogItemProps> = ({
  entry,
  variant = "default",
  showMetadata = true,
  showChanges = true,
  showIpAddress = false,
}) => {
  const getActivityIcon = (action: string, resourceType: string) => {
    if (action.includes("upload") || resourceType === "file") {
      return <Upload className="h-5 w-5 text-blue-500" />;
    }
    if (action.includes("form") || resourceType === "form_submission") {
      return <FileText className="h-5 w-5 text-green-500" />;
    }
    if (action.includes("user") || action.includes("assign")) {
      return <UserPlus className="h-5 w-5 text-purple-500" />;
    }
    if (action.includes("folder") || resourceType === "folder") {
      return <Folder className="h-5 w-5 text-yellow-500" />;
    }
    if (action.includes("link") || resourceType === "case_form") {
      return <LinkIcon className="h-5 w-5 text-orange-500" />;
    }
    return <Activity className="h-5 w-5 text-gray-500" />;
  };

  const formatActionDescription = (
    entry: AuditLogEntry,
    includeField: boolean = false,
  ) => {
    const user = entry.userFullName || entry.userEmail || "Unknown user";
    const action = entry.action.toLowerCase();

    // Extract field that was changed from changes object
    let fieldChanged = "";
    if (
      includeField &&
      entry.changes &&
      Object.keys(entry.changes).length > 0
    ) {
      const changedFields = Object.keys(entry.changes);
      if (changedFields.length === 1) {
        fieldChanged = ` (${changedFields[0]})`;
      } else if (changedFields.length > 1) {
        fieldChanged = ` (${changedFields.join(", ")})`;
      }
    }

    switch (entry.resourceType) {
      case "file":
        if (action.includes("upload")) return `${user} uploaded a file`;
        if (action.includes("delete")) return `${user} deleted a file`;
        if (action.includes("download")) return `${user} downloaded a file`;
        break;
      case "form_submission":
        return `${user} submitted a form`;
      case "folder":
        if (action.includes("create")) return `${user} created a folder`;
        if (action.includes("delete")) return `${user} deleted a folder`;
        break;
      case "case_form":
        if (action.includes("create")) return `${user} created a case form`;
        if (action.includes("delete")) return `${user} deleted a case form`;
        break;
      case "case_assignment":
        if (action.includes("assign"))
          return `${user} was assigned to the case`;
        if (action.includes("remove"))
          return `${user} was removed from the case`;
        break;
      case "case":
        if (action.includes("update"))
          return `${user} updated the case${fieldChanged}`;
        if (action.includes("close")) return `${user} closed the case`;
        break;
    }

    return `${user} performed ${entry.action} on ${entry.resourceType}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Minimal variant - simple dot with text
  if (variant === "minimal") {
    return (
      <div className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0 dark:border-gray-800">
        <div className="mt-1 h-2 w-2 rounded-full bg-brand-500 flex-shrink-0"></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 dark:text-gray-100">
            {formatActionDescription(entry, true)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(entry.createdAt)}
          </p>
        </div>
      </div>
    );
  }

  // Audit variant - action-specific colored icons in a Card
  if (variant === "audit") {
    const ActionIcon = actionIcons[entry.action] || FileText;
    const actionColorClass =
      actionColorClasses[entry.action] ||
      "text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-800/20 dark:border-gray-700 dark:text-gray-400";

    return (
      <Card size="small">
        <div className="grid grid-cols-[auto_1fr_auto] items-start gap-4">
          {/* Icon & Action */}
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg border flex-shrink-0 ${actionColorClass}`}
          >
            <ActionIcon size={20} />
          </div>

          {/* Details */}
          <div className="min-w-0 text-sm">
            <div className="mb-1 font-semibold text-black dark:text-white text-sm">
              {toTitleCase(entry.action.replace(/_/g, " "))} -{" "}
              {entry.resourceType}
              {entry.userId === null && (
                <span className="ml-2 inline-flex items-center rounded border border-yellow-300 bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                  External Access
                </span>
              )}
            </div>
            <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
              By: {entry.userEmail || entry.userFullName || "External User"}{" "}
              {entry.ipAddress && `(${entry.ipAddress})`}
            </div>
            {entry.metadata?.caseId && (
              <div className="text-xs text-gray-500 dark:text-gray-500">
                Case ID: {entry.metadata.caseId}
              </div>
            )}
            {entry.metadata?.filename && (
              <div className="text-xs text-gray-500 dark:text-gray-500">
                File: {entry.metadata.filename}
              </div>
            )}
            {entry.metadata?.verifiedEmail && (
              <div className="text-xs text-gray-500 dark:text-gray-500">
                Email: {entry.metadata.verifiedEmail}
              </div>
            )}
            {entry.metadata?.participantEmail && (
              <div className="text-xs text-gray-500 dark:text-gray-500">
                Submitted by: {entry.metadata.participantEmail}
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 flex-shrink-0">
            <Calendar size={14} />
            {formatTimestamp(entry.createdAt)}
          </div>
        </div>
      </Card>
    );
  }

  // Default variant - full activity display with icon, metadata, and changes
  return (
    <div className="flex gap-4 py-4 first:pt-0 last:pb-0">
      {/* Icon */}
      <div className="flex-shrink-0">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
          {getActivityIcon(entry.action, entry.resourceType)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatActionDescription(entry)}
            </p>

            {/* Metadata */}
            {showMetadata &&
              entry.metadata &&
              Object.keys(entry.metadata).length > 0 && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {Object.entries(entry.metadata).map(([key, value]) => (
                    <span key={key} className="mr-3">
                      {key}: {String(value)}
                    </span>
                  ))}
                </div>
              )}

            {/* Changes */}
            {showChanges &&
              entry.changes &&
              Object.keys(entry.changes).length > 0 && (
                <div className="mt-2 rounded-lg bg-gray-50 p-2 text-xs dark:bg-gray-900/50">
                  <p className="font-medium text-gray-700 dark:text-gray-300">
                    Changes:
                  </p>
                  {Object.entries(entry.changes).map(([key, value]) => (
                    <div
                      key={key}
                      className="mt-1 text-gray-600 dark:text-gray-400"
                    >
                      <span className="font-medium">{key}:</span>{" "}
                      {JSON.stringify(value)}
                    </div>
                  ))}
                </div>
              )}

            {/* IP and User Agent (if available) */}
            {showIpAddress && entry.ipAddress && (
              <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                IP: {entry.ipAddress}
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div className="flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(entry.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogItem;
